import { useCallback } from 'react';

import type { MessageActionResolutions } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { isEmptyConversation } from '../../../../controllers/chat-store/isEmptyConversation';
import { useChatStoreActions } from '../../../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../../../controllers/conversation/useConversation';

/**
 * Provides functions to resolve one or multiple assistant actions for a specific message.
 *
 * @param messageId - The ID of the message whose actions are to be resolved.
 * @returns An object containing:
 *   - resolveAction: Resolves a single action by its key, resolution type, and invocation ID.
 *   - resolveActions: Resolves multiple actions by key, resolution type, and a list of invocation IDs.
 *
 * Useful for handling the acceptance, decline, or execution of assistant actions (e.g., Jira, JSM actions)
 * within the conversational context.
 */
export const useResolveActions = (
	messageId: string,
): {
	resolveAction: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationId: string,
		data?: unknown,
	) => Promise<undefined>;
	resolveActions: (
		actionKey: string,
		actionResolution: MessageActionResolutions,
		invocationIds: string[],
		data?: unknown,
	) => void;
} => {
	const { resolveConversationAction } = useChatStoreActions();
	const { conversation } = useConversation();

	const conversationId = !isEmptyConversation(conversation?.id) ? conversation?.id : undefined;

	const resolveAction = useCallback(
		(
			actionKey: string,
			actionResolution: MessageActionResolutions,
			invocationId: string,
			data?: unknown,
		): Promise<undefined> =>
			resolveConversationAction({
				conversationId: conversationId ?? '',
				messageId,
				payload: {
					context: {
						key: actionKey,
						invocationId,
						messageId,
						data,
						resolution: {
							type: actionResolution,
						},
					},
				},
				skipSuccessOptimisticAssistantResponse: true,
			}),
		[resolveConversationAction, messageId, conversationId],
	);

	const resolveActions = useCallback(
		(
			actionKey: string,
			actionResolution: MessageActionResolutions,
			invocationIds: string[],
			data?: unknown,
		): void =>
			invocationIds.forEach((invocationId) =>
				resolveAction(actionKey, actionResolution, invocationId, data),
			),
		[resolveAction],
	);

	return { resolveAction, resolveActions };
};
